import React, {PureComponent, Fragment} from 'react';

import {type Post, type PostEmbed} from 'mattermost-redux/types/posts';

import {
    getEmbedsFromMetadata,
} from 'mattermost-redux/utils/post_utils';
import {type AppBinding} from 'mattermost-redux/types/apps';

import MessageAttachmentList from 'components/post_view/message_attachments/message_attachment_list';
import PostAttachmentOpenGraph from 'components/post_view/post_attachment_opengraph';
import PostImage from 'components/post_view/post_image';
import YoutubeVideo from 'components/youtube_video';

import {type PostWillRenderEmbedPluginComponent} from 'types/store/plugins';
import EmbeddedBindings from '../embedded_bindings/embedded_bindings';
import {type TextFormattingOptions} from 'utils/text_formatting';
import PostMessagePreview from 'components/post_view/post_message_preview';
import PostQuote from 'components/post_view/post_quote';
import type {MessageAttachment} from '@mattermost/types/message_attachments';
import type {PostQuote as QuoteMetadata} from 'mattermost-redux/types/posts';

export type Props = {
    post: Post;
    quote?: Post;
    quoteMetadata?: QuoteMetadata;
    pluginPostWillRenderEmbedComponents?: PostWillRenderEmbedPluginComponent[];
    children?: JSX.Element;
    isEmbedVisible?: boolean;
    options?: Partial<TextFormattingOptions>;
    appsEnabled: boolean;
    allowMultipleEmbeds?: boolean;
    handleFileDropdownOpened?: (open: boolean) => void;
    actions: {
        toggleEmbedVisibility: (id: string) => void;
    };
};

export default class PostBodyAdditionalContent extends PureComponent<Props> {
    toggleEmbedVisibility = () => {
        this.props.actions.toggleEmbedVisibility(this.props.post.id);
    }

    isEmbedToggleable = (embed: PostEmbed) => {
        const postWillRenderEmbedComponents = this.props.pluginPostWillRenderEmbedComponents || [];
        for (const c of postWillRenderEmbedComponents) {
            if (c.match(embed)) {
                return Boolean(c.toggleable);
            }
        }

        return embed.type === 'image' || (embed.type === 'opengraph' && YoutubeVideo.isYoutubeLink(embed.url));
    }

    renderEmbed = (embed: PostEmbed) => {
        const postWillRenderEmbedComponents = this.props.pluginPostWillRenderEmbedComponents || [];
        for (const c of postWillRenderEmbedComponents) {
            if (c.match(embed)) {
                const Component = c.component;
                return this.props.isEmbedVisible && <Component embed={embed}/>;
            }
        }
        switch (embed.type) {
        case 'image':
            if (!this.props.isEmbedVisible) {
                return null;
            }

            return (
                <PostImage
                    imageMetadata={this.props.post.metadata.images[embed.url]!}
                    link={embed.url}
                    post={this.props.post}
                />
            );

        case 'message_attachment': {
            let attachments: MessageAttachment[] = [];
            if (this.props.post.props && this.props.post.props.attachments) {
                attachments = this.props.post.props.attachments;
            }

            return (
                <MessageAttachmentList
                    attachments={attachments}
                    postId={this.props.post.id}
                    channelId={this.props.post.channel_id}
                    options={this.props.options}
                    imagesMetadata={this.props.post.metadata.images}
                    truncateTextAttachment={this.props.post.type === 'slack_attachment'}
                />
            );
        }

        case 'opengraph':
            if (YoutubeVideo.isYoutubeLink(embed.url)) {
                if (!this.props.isEmbedVisible) {
                    return null;
                }

                return (
                    <YoutubeVideo
                        postId={this.props.post.id}
                        link={embed.url}
                        show={this.props.isEmbedVisible}
                    />
                );
            }

            return (
                <PostAttachmentOpenGraph
                    postId={this.props.post.id}
                    link={embed.url}
                    isEmbedVisible={this.props.isEmbedVisible}
                    post={this.props.post}
                    toggleEmbedVisibility={this.toggleEmbedVisibility}
                />
            );
        case 'permalink':
            if (embed.data && 'post_id' in embed.data && embed.data.post_id) {
                return (
                    <PostMessagePreview
                        metadata={embed.data}
                        previewPost={embed.data.post}
                        handleFileDropdownOpened={this.props.handleFileDropdownOpened}
                    />
                );
            }
            return null;
        default:
            return null;
        }
    }

    get renderQuote() {
        const {quote, quoteMetadata} = this.props;
        const channelType = quoteMetadata?.data?.channel_type;
        const teamName = quoteMetadata?.data?.team_name;

        if (!quote || channelType == null || teamName == null) {
            return null;
        }
        return (
            <PostQuote
                post={quote}
                channelType={channelType}
                teamName={teamName}
                isAdditionalContent={true}
            />
        );
    }

    get renderToggle() {
        return (
            <button
                key='toggle'
                className={'style--none post__embed-visibility color--link'}
                data-expanded={this.props.isEmbedVisible}
                aria-label='Toggle Embed Visibility'
                onClick={this.toggleEmbedVisibility}
            />
        );
    }

    get renderEmbeds() {
        const embeds = getEmbedsFromMetadata(this.props.post.metadata);
        if (embeds.length === 0) {
            return null;
        }

        let isEmbedToggleable = this.isEmbedToggleable(embeds[0]);
        if (!this.props.allowMultipleEmbeds) {
            return (
                <>
                    {isEmbedToggleable && this.renderToggle}
                    {this.renderEmbed(embeds[0])}
                </>
            );
        }

        if (!isEmbedToggleable) {
            isEmbedToggleable = embeds.some(this.isEmbedToggleable);
        }

        return (
            <>
                {isEmbedToggleable && this.renderToggle}
                {embeds.map((embed, index) => (
                    <Fragment key={index}>
                        {this.renderEmbed(embed)}
                    </Fragment>
                ))}
            </>
        );
    }

    render() {
        if (this.props.appsEnabled) {
            if (hasValidEmbeddedBinding(this.props.post.props)) {
                // TODO Put some log / message if the form is not valid?
                return (
                    <Fragment>
                        {this.props.children}
                        <EmbeddedBindings
                            embeds={this.props.post.props.app_bindings}
                            post={this.props.post}
                        />
                    </Fragment>
                );
            }
        }

        return (
            <div>
                {this.renderQuote}
                {this.props.children}
                {this.renderEmbeds}
            </div>
        );
    }
}

function hasValidEmbeddedBinding(props: Record<string, any>) {
    if (!props) {
        return false;
    }

    if (!props.app_bindings) {
        return false;
    }

    const embeds = props.app_bindings as AppBinding[];

    if (!embeds.length) {
        return false;
    }

    return true;
}
